import React, { useCallback, useEffect, useState } from "react";
import {
  Row,
  Label,
  Input,
  Form,
  FormGroup,
  Button,
  FormFeedback,
} from "reactstrap";
import { useFormik } from "formik";
import * as yup from "yup";
import "flatpickr/dist/themes/material_blue.css";
import { useDropzone } from "react-dropzone";
import { setAuthorization } from "../../../helpers/api_helper";
import {
  completeUpload,
  getFundsType,
  getIncomeRanges,
  savePDF,
  uploadChunk,
  uploadPDF,
} from "../../../helpers/AuthType/authBackend";
import { splitFileIntoChunksBase64 } from "shared/fileUtils";
import { BiLoaderAlt, BiCheckCircle } from "react-icons/bi";

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const MAX_PDF_SIZE = 20 * 1024 * 1024; // 20MB

interface BasicInformationProps {
  onSubmit: (values: any) => void;
  onBack: () => void;
  data?: any;
  userId: any;
}

const FinancialInformation = ({
  onSubmit,
  onBack,
  data,
  userId,
}: BasicInformationProps) => {
  document.title = "Ditobanx";

  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
  const [incomeRanges, setIncomeRanges] = useState<any[]>([]);
  const [fundsType, setFundsTypes] = useState<any[]>([]);
  const [validateAddress, setValidateAddress] = useState<boolean>(false);
  const [fileType, setFileType] = useState<string>("");

  const [loadingAddress, setLoadingAddress] = useState(false); // ⏳ spinner

  const uploadFileChunks = async (file: File, type: string) => {
    const chunks = await splitFileIntoChunksBase64(file);
    const uploadId = `${file.name}-${Date.now()}`;
    const totalChunks = chunks.length;

    for (let i = 0; i < totalChunks; i++) {
      const payload = {
        uploadId,
        filename: file.name,
        chunkIndex: i,
        totalChunks,
        chunkBase64: chunks[i],
        type, // Ej: "selfie", "front", etc.
      };

      await uploadChunk(payload);
    }

    // Notificamos que se terminó de subir
    await completeUpload({
      uploadId,
      filename: file.name,
      type,
      userId,
    });

    return uploadId;
  };

  const uploadPdfChunks = async (file: File, type: any) => {
    const chunks = await splitFileIntoChunksBase64(file);
    const uploadId = `${file.name}-${Date.now()}`;
    const totalChunks = chunks.length;

    for (let i = 0; i < totalChunks; i++) {
      await uploadPDF({
        uploadId,
        filename: file.name,
        chunkIndex: i,
        totalChunks,
        chunkBase64: chunks[i],
        type,
      });
    }

    // Finalizar ensamblaje
    const response = await savePDF({
      uploadId,
      filename: file.name,
      type,
      userId,
    });

    return response.data.filename; // nombre del PDF guardado
  };

  const validarPDFUploaded = async (type: string) => {
    if (type !== "recibo") return;

    try {
      setLoadingAddress(true); // ⏳ empieza
      if (fileType.includes("image/")) {
        await uploadFileChunks(selectedFiles[0], type);
      } else {
        await uploadPdfChunks(selectedFiles[0], type);
      }
      setValidateAddress(true); // ✅ validado
    } catch (err) {
      console.error(err);
      alert("Ocurrió un error al subir el documento.");
    } finally {
      setLoadingAddress(false); // 🔚 detiene
    }
  };

  const formik: any = useFormik({
    initialValues: {
      monthlyIncome: "", // corregido de "mounthlyIncome"
      incomeOrigin: "",
      acceptDeclaration: false,
    },
    validationSchema: yup.object().shape({
      monthlyIncome: yup.string().required("Please select your Monthly Income"),
      incomeOrigin: yup.string().required("Please select your Income Origin"),
      acceptDeclaration: yup
        .boolean()
        .oneOf([true], "You must accept the declaration"),
    }),
    onSubmit: async (values: any) => {
      const newValues = {
        monthly_income: Number(values.monthlyIncome),
        income_origin: Number(values.incomeOrigin),
        acceptDeclaration: values.acceptDeclaration,
      };

      if (validateAddress) {
        onSubmit(newValues);
      } else {
        alert("Debes validar el documento cargado");
      }
    },
  });

  const onDropDocs = useCallback((acceptedFiles: any[]) => {
    const validFiles = acceptedFiles.filter((file) => {
      setFileType(file.type);
      const typeFile: string = file.type;
      if (file.type === "image/png" && file.size > MAX_FILE_SIZE) {
        alert(
          `El archivo ${file.name} supera el tamaño máximo permitido (2 MB).`
        );
        return false;
      }
      if (typeFile.includes("application/") && file.size > MAX_PDF_SIZE) {
        alert(
          `El archivo ${file.name} supera el tamaño máximo permitido (20 MB).`
        );
        return false;
      }
      return true;
    });
    const mappedFiles = validFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    );
    setSelectedFiles((prevFiles) => [...prevFiles, ...mappedFiles]);
  }, []);

  const { getInputProps: getDocInputProps, open: openDoc } = useDropzone({
    onDrop: onDropDocs,
    noClick: true,
    noKeyboard: true,
    accept: { "image/*": [], "application/pdf": [] },
    maxFiles: 1,
  });

  const removeDoc = (idx: number) => {
    setSelectedFiles((p) => p.filter((_, i) => i !== idx));
    setValidateAddress(false); // vuelve a requerir verificación
    setLoadingAddress(false); // por si estaba en curso
  };

  const getData = async () => {
    const responseIncomeRanges = await getIncomeRanges();
    setIncomeRanges(JSON.parse(responseIncomeRanges.data));
    const responseFundTypes = await getFundsType(2);
    // console.log(JSON.parse(responseFundTypes.data));
    setFundsTypes(JSON.parse(responseFundTypes.data));
  };

  useEffect(() => {
    const responsePublicToken = localStorage.getItem("publicToken");
    setAuthorization(responsePublicToken);
    getData();
  }, []);

  return (
    <React.Fragment>
      <Form onSubmit={formik.handleSubmit} autoComplete="off">
        <Row>
          {/* INGRESO MENSUAL */}
          <FormGroup className="mb-3">
            <Label htmlFor="monthlyIncome">Ingreso mensual estimado</Label>
            <Input
              type="select"
              id="monthlyIncome"
              name="monthlyIncome"
              value={formik.values.monthlyIncome}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={
                formik.touched.monthlyIncome && !!formik.errors.monthlyIncome
              }
            >
              <option value="" disabled>
                Elige una opción
              </option>
              {incomeRanges.map((income) => (
                <option key={income.id} value={income.id}>
                  {income.name}
                </option>
              ))}
            </Input>
            {formik.touched.monthlyIncome && formik.errors.monthlyIncome && (
              <FormFeedback>{formik.errors.monthlyIncome}</FormFeedback>
            )}
          </FormGroup>

          {/* ORIGEN DE FONDOS */}
          <FormGroup tag="fieldset" className="mb-4">
            <legend className="col-form-label" style={{ fontSize: "15px" }}>
              Origen de fondos
            </legend>
            <div style={{ marginLeft: "20px", fontSize: "14px" }}>
              {fundsType.map((item) => (
                <FormGroup check key={item.id}>
                  <Label check>
                    <Input
                      type="radio"
                      name="incomeOrigin"
                      value={String(item.id)}
                      onChange={formik.handleChange}
                      checked={formik.values.incomeOrigin === String(item.id)}
                    />{" "}
                    {item.name}
                  </Label>
                </FormGroup>
              ))}
            </div>
            {formik.touched.incomeOrigin && formik.errors.incomeOrigin && (
              <div className="text-danger">{formik.errors.incomeOrigin}</div>
            )}
          </FormGroup>

          {/* DOCUMENTO DOMICILIO */}
          <FormGroup
            className="mb-3"
            style={{
              backgroundColor: "#f7f7f7",
              padding: "10px",
              borderRadius: "10px",
            }}
          >
            <Label>Prueba de domicilio</Label>
            <p className="text-muted">
              Sube un recibo reciente (luz, agua, gas) o contrato de alquiler
            </p>

            <div
              className="d-flex flex-column align-items-center p-4 border rounded bg-light"
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.preventDefault();
                openDoc();
              }}
            >
              <i
                className="bx bx-upload"
                style={{ fontSize: "48px", color: "#007bff" }}
              />
              <h5 className="mt-2 mb-3 text-secondary">
                Selecciona una imagen o PDF
              </h5>

              {/* Input oculto controlado por Dropzone */}
              <input {...getDocInputProps()} style={{ display: "none" }} />

              <Button color="primary" size="sm">
                <i className="bx bx-upload mr-1" /> Subir Archivo
              </Button>

              {/* Previsualización dentro del mismo contenedor */}
              {selectedFiles.length > 0 && (
                <div className="mt-3 w-100 d-flex flex-wrap justify-content-center">
                  {selectedFiles.map((file: any, index: number) => (
                    <div
                      key={index}
                      className="position-relative m-2 p-3 border rounded bg-white"
                      style={{ width: "180px" }}
                    >
                      {/* Botón para eliminar */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeDoc(index);
                        }}
                        className="position-absolute btn btn-sm btn-danger rounded-circle"
                        style={{
                          top: "-10px",
                          right: "-10px",
                          width: "24px",
                          height: "24px",
                          padding: 0,
                          fontSize: "14px",
                          lineHeight: "1",
                        }}
                      >
                        &times;
                      </button>

                      {/* Miniatura: si es imagen, mostrar <img>; si es PDF, mostrar icono */}
                      {file.type.startsWith("image/") ? (
                        <img
                          src={file.preview}
                          alt={file.name}
                          className="img-fluid mb-2"
                          style={{ maxHeight: "120px", borderRadius: "8px" }}
                        />
                      ) : (
                        <div className="d-flex justify-content-center mb-2">
                          <i
                            className="bx bx-file-pdf"
                            style={{ fontSize: "36px", color: "#d9534f" }}
                          />
                        </div>
                      )}

                      {/* Nombre del archivo truncado */}
                      <p
                        className="text-truncate text-center mb-0"
                        style={{ maxWidth: "100%" }}
                        title={file.name}
                      >
                        {file.name}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {selectedFiles.length > 0 && (
                <Button
                  type="button"
                  color={validateAddress ? "success" : "primary"}
                  disabled={validateAddress || loadingAddress}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    validarPDFUploaded("recibo");
                  }}
                  className="mt-3 flex items-center gap-2"
                >
                  {loadingAddress && <BiLoaderAlt className="animate-spin" />}
                  {validateAddress && !loadingAddress && (
                    <BiCheckCircle className="text-green-500" />
                  )}
                  {loadingAddress
                    ? "Cargando…"
                    : validateAddress
                    ? "Validado"
                    : "Verificar"}
                </Button>
              )}
            </div>
          </FormGroup>

          {/* DECLARACIÓN */}
          <FormGroup className="mb-4">
            {/* Título */}
            <Label tag="h6">Declaración de Origen de Fondos</Label>
            {/* Texto largo con scroll */}
            <div
              style={{
                maxHeight: "120px",
                overflowY: "auto",
                padding: "8px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                backgroundColor: "#f9f9f9",
              }}
            >
              <p>
                Yo, declaro bajo juramento, que los fondos o valores que
                entregaré a DITOBANX EL SALVADOR, S.A. DE C.V. los cuales tienen
                un origen y destino lícito y que de ninguna manera están
                relacionados con los delitos generadores de Lavado de Dinero y
                Activos descritos en el artículo 6 de la ley contra el lavado de
                Dinero y Activos, con el financiamiento del Terrorismo, ni con
                ningún tipo de actividad ilícita. Declaro que la información
                suministrada en este formulario es verdadera, es completa y se
                encuentra actualizada a la fecha de hoy. Además, acepto que
                cualquier declaración falsa o inexactitud en el contenido del
                presente documento será causa de terminación inmediata de
                cualquier acuerdo o relación comercial actualmente existente o
                futura con DITOBANX EL SALVADOR, S.A. DE C.V., así mismo
                autorizo a DITOBANX EL SALVADOR, S.A DE C.V. para que pueda
                confirmar la veracidad de la información proporcionada. Además,
                declaro que la persona jurídica que represento, miembros del
                órgano de administración superior, accionistas, representantes
                legales y administradores, no estamos incluidos en los listados
                de la Oficina del Control de Activos Extranjeros (OFAC), o en
                otro listado de similar naturaleza, o en algún listado de
                carácter nacional y/o internacional en la que se publiquen
                nombres de personas o entidades a quienes se les haya iniciado
                procesos judiciales, actuación administrativa o que hayan sido
                sancionados y/o condenados por las autoridades con actividades
                ilegales relacionadas al lavado de dinero y/o financiamiento del
                terrorismo.
              </p>
            </div>
            {/* Checkbox */}
            <FormGroup check className="mb-3 mt-3">
              <Label check>
                <Input
                  type="checkbox"
                  name="acceptDeclaration"
                  onChange={formik.handleChange}
                  checked={formik.values.acceptDeclaration}
                />{" "}
                Acepto la Declaración jurada del{" "}
                <span
                  style={{ color: "blue", textDecorationLine: "underline" }}
                >
                  origen de los fondos
                </span>
              </Label>
              {formik.touched.acceptDeclaration &&
                formik.errors.acceptDeclaration && (
                  <div className="text-danger">
                    {formik.errors.acceptDeclaration}
                  </div>
                )}
            </FormGroup>
          </FormGroup>
        </Row>

        {/* BOTONES */}
        <div
          style={{
            marginTop: "20px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          {/* <button
            type="button"
            className="btn btn-secondary me-2"
            onClick={onBack}
          >
            Regresar
          </button> */}
          <Button type="submit" className="btn btn-primary">
            Siguiente
          </Button>
        </div>
      </Form>
    </React.Fragment>
  );
};

export default FinancialInformation;
